import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NamedLink } from '../../components';
import ListingCard from '../../components/ListingCard/ListingCard';

import css from './SectionSimilarItems.module.css';

/**
 * SectionSimilarItems displays a horizontal carousel of similar listings
 * based on the current listing's category.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS class
 * @param {string} [props.rootClassName] - Override root CSS class
 * @param {Array<Object>} props.listings - Array of similar listing entities
 * @param {boolean} [props.isLoading=false] - Whether similar items are loading
 * @param {Object} [props.currentUser] - Current user entity
 * @param {Function} [props.onUpdateFavorites] - Callback for updating favorites
 * @returns {JSX.Element} Similar items carousel section
 */
const SectionSimilarItems = props => {
  const {
    className,
    rootClassName,
    listings = [],
    isLoading = false,
    currentUser,
    onUpdateFavorites,
  } = props;

  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll state
  const checkScrollState = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollState();
    window.addEventListener('resize', checkScrollState);
    return () => window.removeEventListener('resize', checkScrollState);
  }, [listings]);

  const scrollAmount = 320; // Approximately one card width + gap

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      setTimeout(checkScrollState, 300);
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkScrollState, 300);
    }
  };

  const handleScroll = () => {
    checkScrollState();
  };

  const classes = classNames(rootClassName || css.root, className);

  // Don't render if no similar items and not loading
  if (!isLoading && (!listings || listings.length === 0)) {
    return null;
  }

  return (
    <section className={classes}>
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="SectionSimilarItems.title" />
        </h2>
        {listings.length > 4 && (
          <div className={css.navButtons}>
            <button
              className={classNames(css.navButton, { [css.navButtonDisabled]: !canScrollLeft })}
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
            >
              <ChevronLeft className={css.navIcon} />
            </button>
            <button
              className={classNames(css.navButton, { [css.navButtonDisabled]: !canScrollRight })}
              onClick={scrollRight}
              disabled={!canScrollRight}
              aria-label="Scroll right"
            >
              <ChevronRight className={css.navIcon} />
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className={css.loadingContainer}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={css.loadingCard}>
              <div className={css.loadingImage} />
              <div className={css.loadingText} />
              <div className={css.loadingTextShort} />
            </div>
          ))}
        </div>
      ) : (
        <div className={css.carousel} ref={carouselRef} onScroll={handleScroll}>
          {listings.map(listing => (
            <div key={listing.id.uuid} className={css.cardWrapper}>
              <ListingCard
                className={css.listingCard}
                listing={listing}
                showAuthorInfo={false}
                currentUser={currentUser}
                onUpdateFavorites={onUpdateFavorites}
              />
            </div>
          ))}
        </div>
      )}

      {/* Gradient overlays for scroll indication */}
      {canScrollLeft && <div className={css.gradientLeft} />}
      {canScrollRight && <div className={css.gradientRight} />}
    </section>
  );
};

export default SectionSimilarItems;
